import React, { useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

interface Feature {
  name: string;
  tooltip: string;
  values: (string | boolean)[];
}

const PricingComparison = () => {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  
  const features: Feature[] = [
    {
      name: "Monthly Cost",
      tooltip: "The monthly subscription cost for each plan tier",
      values: ["$0", "$9", "$29", "$59"]
    },
    {
      name: "Meetings per Month",
      tooltip: "The total number of meetings you can record and transcribe each month",
      values: ["Up to 3", "Up to 10", "Up to 30", "Unlimited"]
    },
    {
      name: "Meeting Duration",
      tooltip: "The maximum length allowed for each meeting recording",
      values: ["30 min/meeting", "60 min/meeting", "90 min/meeting", "120 min/meeting"]
    },
    {
      name: "Transcription",
      tooltip: "Converts meeting audio into text for easier review and reference",
      values: ["Basic", "Advanced", "Advanced + Speaker ID", "Advanced + Speaker ID"]
    },
    {
      name: "Meeting Summaries",
      tooltip: "Provides a concise recap of meeting highlights, tasks, and decisions",
      values: ["Basic", "Detailed", "Comprehensive", "Comprehensive + Insights"]
    },
    {
      name: "Sentiment Analysis",
      tooltip: "Evaluates participant tone and engagement to uncover key dynamics",
      values: [false, false, true, true]
    },
    {
      name: "Decision Tracking",
      tooltip: "Identifies and tracks decisions made during meetings for easy follow-up",
      values: [false, false, true, true]
    },
    {
      name: "Action Items",
      tooltip: "Create, edit, and track tasks from your meetings",
      values: ["Manual only", "Automated extraction", "Advanced with steps", "Advanced + AI suggestions"]
    },
    //{
   //   name: "AI Chatbot",
     // tooltip: "A personal AI assistant to help answer questions and generate insights",
     // values: [false, "5 queries/day", "15 queries/day", "Unlimited"]
    //},
   // {
   //   name: "Knowledge Base",
   //   tooltip: "Upload materials and documents to enhance AI-driven responses and insights",
   //   values: [false, false, "Upload docs", "Upload + Auto-integration"]
   // },
   // {
    //  name: "Tagging",
    //  tooltip: "Organize meetings, tasks, and notes with custom tags and categories",
    //  values: ["Basic", "Manual", "Advanced", "Automatic"]
    //},
    {
      name: "Storage",
      tooltip: "The amount of space available for saving meeting recordings and notes",
      values: ["1GB", "5GB", "25GB", "50GB"]
    },
    {
      name: "Retention",
      tooltip: "The duration your data is stored before being deleted or archived",
      values: ["30 days", "60 days", "Unlimited", "Unlimited"]
    }
  ];

  const plans = ["Free", "Basic", "Pro", "Premium"];

  const handleTooltipClick = (featureName: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (openTooltip === featureName) {
      setOpenTooltip(null);
    } else {
      setOpenTooltip(featureName);
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <h1 className="text-4xl font-bold text-center mb-4">Still Not Sure? Here's the Breakdown</h1>
      <p className="text-xl text-muted-foreground text-center mb-12">
        Compare plans and pick what suits your vibe
      </p>
      
      <div className="overflow-x-auto relative">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-background">
            <tr className="divide-x divide-border [&>*:first-child]:border-r-border">
              <th className="text-left p-2 sticky left-0 z-20 text-sm bg-background">Feature</th>
              {plans.map((plan, index) => (
                <th key={index} className="p-2 text-left text-sm font-semibold bg-background">
                  {plan}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {features.map((feature, index) => (
              <tr key={index} className="divide-x divide-border [&>*:first-child]:border-r-border">
                <td className="p-2 font-medium sticky left-0 text-sm bg-background">
                  <TooltipProvider delayDuration={0}>
                    <Tooltip open={openTooltip === feature.name}>
                      <TooltipTrigger asChild touch>
                        <span 
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleTooltipClick(feature.name, e)}
                          className="inline border-b border-dashed border-muted-foreground cursor-help"
                        >
                          {feature.name}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="right" 
                        className="max-w-[300px] text-sm"
                        sideOffset={5}
                        onPointerDownOutside={() => setOpenTooltip(null)}
                      >
                        {feature.tooltip}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </td>
                {feature.values.map((value, valueIndex) => (
                  <td key={valueIndex} className="p-2 text-sm bg-background">
                    {typeof value === "boolean" ? (
                      value ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )
                    ) : (
                      <span className="whitespace-pre-line">{value}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-12 text-center">
        <Button asChild size="lg">
          <Link to="/sign-up">Choose Your Plan</Link>
        </Button>
      </div>
    </div>
  );
};

export default PricingComparison;